import { AppLauncher } from '@capacitor/app-launcher';
import { trackOutboundClick } from '@/utils/analytics';
import { normalizeExternalUrl } from '@/utils/browser-helper';
import { isNativeNotWeb } from '@/utils/platform-check';

/**
 * Hand a URL to the OS so the destination's own app can claim it.
 *
 * This is the ONLY correct way to open an EXTERNAL deep-link target, and it has now
 * been got wrong twice in two different places, so it lives in one:
 *
 *   - `window.open(url, '_system')` is served in an in-app browser. It shows the web
 *     page and never passes the universal link on, so a YouVersion reading-plan QR
 *     opened bible.com instead of the app — the one thing an EXTERNAL target exists
 *     to do. A comment in the resource gateway asserted the opposite and cost the
 *     hand-off a second time; the empirical answer from device testing on
 *     2026-09-01 is that it does not work.
 *   - `openExternal(url, '_blank')` goes through `window.open` too, so it has the
 *     same problem.
 *
 * `AppLauncher.openUrl` goes through `UIApplication.open` / `Intent.ACTION_VIEW`,
 * where the OS picks the handler: YouVersion if installed, the browser if not. It
 * also needs no user gesture, which matters because both callers run from a
 * lifecycle hook rather than a tap — a popup blocked for that reason is
 * indistinguishable from a hand-off that worked, since both leave the app on /home.
 *
 * The plugin is registered on both platforms and has been in `ios/App/Podfile` since
 * `prod-v1.2.12-221`, so this ships as a live update without a store release.
 *
 * Returns whether the URL was handed off, so a caller can tell a refusal from a
 * success rather than assuming.
 */
export async function handOffToOs(url, metadata = {}) {
  const normalizedUrl = normalizeExternalUrl(url);

  if (!normalizedUrl) {
    console.warn('[Handoff] Skipping invalid external URL', { source: metadata.source });
    return false;
  }

  // Tracked here rather than in each caller, so a hand-off cannot be counted twice
  // or — as happened when the gateway called window.open directly — not at all.
  trackOutboundClick({ url: normalizedUrl, linkType: 'external_url', ...metadata });

  if (isNativeNotWeb()) {
    try {
      await AppLauncher.openUrl({ url: normalizedUrl });
      return true;
    } catch (error) {
      console.warn('[Handoff] AppLauncher refused the hand-off:', error?.message);
    }
  }

  // Web and dev builds have no AppLauncher, and no installed app to claim the link
  // anyway. A new window is the closest equivalent.
  return Boolean(window.open(normalizedUrl, '_system'));
}
